import { postRequest, getRequest } from './apiService';
import type { ApiServiceConfig } from './apiService';
import type {
  FindBNsResponse,
  FindOperatorsResponse,
  CalculateLostResponse,
  CheckAPIKeyHealthResponse,
} from '../store/types';
import { API_CONSTANTS } from '../constants/apiConstants';
import { ERROR_MESSAGES } from '../constants/errorConstants';

// Parameter interfaces for API service methods
interface FindBNsParams {
  operator: string;
  latitude: number;
  longitude: number;
  radius: number;
  operatorKey?: string;
  optionsParams?: string;
  signal?: AbortSignal;
}

interface CheckAPIKeyHealthParams {
  operatorKey?: string;
  email?: string;
  signal?: AbortSignal;
}

interface FindOperatorsParams {
  address?: string;
  state?: string;
  country?: string;
  operatorKey?: string;
  signal?: AbortSignal;
}

interface LoadCountryListParams {
  signal?: AbortSignal;
}

interface CalculateLostParams {
  baseNodeData?: any;
  remoteNodeData: any;
  operator?: string;
  email?: string;
  operatorKey?: string;
  isMultiSector?: boolean;
  signal?: AbortSignal;
}

interface MakeSapRequestParams {
  urlPart: string;
  jsonData?: any;
  apiKey?: string;
  additionalOptions?: Record<string, any>;
}

interface GetSVGDataParams {
  url: string;
  signal?: AbortSignal;
}

interface ApiResponse {
  detail?: string;
  [key: string]: any;
}

interface FindBNsApiResponse extends ApiResponse {
  bn_list?: any;
  suffixes?: any;
  legends?: any;
}

interface FindOperatorsApiResponse extends ApiResponse {
  operator_list?: Array<{ name: string }>;
}

interface LoadCountryListApiResponse extends ApiResponse {
  region_list?: any[];
}

interface CalculateLostApiResponse extends ApiResponse {
  sector_list?: any;
}

// Internal storage for operator key
let storedOperatorKey: string | null = null;

export const servicePredictorApiService = {
  /**
   * Initialize the service with an operator key
   * This should be called after successful authentication
   */
  initialize: (operatorKey: string) => {
    storedOperatorKey = operatorKey;
  },

  /**
   * Clear the stored operator key
   * This should be called when logging out or clearing credentials
   */
  clearOperatorKey: () => {
    storedOperatorKey = null;
  },

  /**
   * Get the currently stored operator key
   */
  getOperatorKey: (): string | null => {
    return storedOperatorKey;
  },
  /**
   * Find base nodes by radius
   */
  findBNs: async ({
    operator,
    latitude,
    longitude,
    radius,
    operatorKey,
    optionsParams = '',
    signal,
  }: FindBNsParams): Promise<FindBNsResponse> => {
    const keyToUse = operatorKey || storedOperatorKey;
    if (!operator || !latitude || !longitude || !radius || !keyToUse) {
      throw new Error(ERROR_MESSAGES.MISSING_REQUIRED_PARAMETERS);
    }

    const urlPart = API_CONSTANTS.ENDPOINTS.FIND_BNS_BY_RADIUS;
    const jsonObject: any = {
      operator,
      latitude: parseFloat(String(latitude)),
      longitude: parseFloat(String(longitude)),
      radius,
    };

    let byRadiusUrl = `${urlPart}?_t=${Date.now()}`;
    // If optionsParams is provided, use it
    // Otherwise, use default options
    const options = optionsParams || API_CONSTANTS.DEFAULT_OPTIONS;

    byRadiusUrl += `&options=${encodeURIComponent(options)}`;

    const requestData = {
      url_part: byRadiusUrl,
      api_key: keyToUse,
      json_data: jsonObject,
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...(signal && { signal }),
    };

    try {
      const response = (await postRequest({
        config,
      })) as FindBNsApiResponse;

      if (response.detail) {
        return { error: response.detail };
      }

      if (response.bn_list) {
        jsonObject.bn_list = response.bn_list;
        // Preserve additional fields from response (suffixes, legends, etc.)
        if (response.suffixes) {
          jsonObject.suffixes = response.suffixes;
        }
        if (response.legends) {
          jsonObject.legends = response.legends;
        }
        return { baseNodeList: jsonObject };
      }

      return response as FindBNsResponse;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true };
      }
      console.error('Error in findBNs:', error);
      throw error;
    }
  },

  /**
   * Check API key health and validate operator
   */
  checkAPIKeyHealth: async ({
    operatorKey,
    email,
    signal,
  }: CheckAPIKeyHealthParams): Promise<CheckAPIKeyHealthResponse> => {
    const keyToUse = operatorKey || storedOperatorKey;
    if (!keyToUse) {
      throw new Error(ERROR_MESSAGES.API_KEY_REQUIRED);
    }

    const urlPart = API_CONSTANTS.ENDPOINTS.HEALTH_API_KEY;
    const requestData = {
      url_part: urlPart,
      api_key: keyToUse,
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...(signal && { signal }),
    };

    try {
      const response = (await postRequest({
        config,
      })) as ApiResponse & { operator?: string };

      if (response.detail) {
        return {
          error: response.detail,
          isValid: false,
        };
      }

      if (!response.operator) {
        return {
          error: 'Invalid API Key',
          isValid: false,
        };
      }

      return {
        operator: response.operator,
        isValid: true,
        isTarana: response.operator === 'Tarana',
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true, isValid: false };
      }
      console.error('Error in checkAPIKeyHealth:', error);
      throw error;
    }
  },

  /**
   * Find operators by address, state, or country
   */
  findOperators: async ({
    address,
    state,
    country,
    operatorKey,
    signal,
  }: FindOperatorsParams): Promise<FindOperatorsResponse> => {
    let urlPart = API_CONSTANTS.ENDPOINTS.FIND_OPERATORS;

    // Build URL based on provided parameters
    if (address) {
      urlPart += `/${encodeURIComponent(address)}`;
    }
    if (state) {
      urlPart += `/state/${encodeURIComponent(state)}`;
    }
    if (country) {
      urlPart += `/country/${encodeURIComponent(country)}`;
    }

    const keyToUse = operatorKey || storedOperatorKey;
    const requestData: any = {
      url_part: `${urlPart}?_t=${Date.now()}`,
      ...(keyToUse && { api_key: keyToUse }),
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...(signal && { signal }),
    };

    try {
      const response = (await postRequest({
        config,
      })) as FindOperatorsApiResponse;

      if (response.detail) {
        return { error: response.detail };
      }

      if (response.operator_list) {
        const operatorNames = response.operator_list.map(
          (operator) => operator.name
        );
        return { operatorNames, operatorList: response.operator_list };
      }

      return response as FindOperatorsResponse;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true };
      }
      console.error('Error in findOperators:', error);
      throw error;
    }
  },

  /**
   * Load list of available countries
   */
  loadCountryList: async ({ signal }: LoadCountryListParams = {}): Promise<{
    countryList?: any[];
    error?: string;
    canceled?: boolean;
  }> => {
    const urlPart = API_CONSTANTS.ENDPOINTS.FIND_OPERATORS_COUNTRY_LIST;
    const requestData = {
      url_part: urlPart,
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...(signal && { signal }),
    };

    try {
      const response = (await postRequest({
        config,
      })) as LoadCountryListApiResponse;

      if (response.detail) {
        return { error: response.detail };
      }

      if (response.region_list) {
        return { countryList: response.region_list };
      }

      return {};
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true };
      }
      console.error('Error in loadCountryList:', error);
      throw error;
    }
  },

  /**
   * Calculate lost sectors prediction
   */
  calculateLost: async ({
    baseNodeData,
    remoteNodeData,
    operator = '',
    email = '',
    operatorKey,
    isMultiSector = false,
    signal,
  }: CalculateLostParams): Promise<CalculateLostResponse> => {
    const keyToUse = operatorKey || storedOperatorKey;
    if (!remoteNodeData || !keyToUse) {
      throw new Error(ERROR_MESSAGES.REMOTE_NODE_DATA_REQUIRED);
    }

    const jsonArray: any = {};

    // Add base node data only for multi-sector predictions if provided, not disabled, and not empty
    if (
      isMultiSector &&
      baseNodeData &&
      Object.keys(baseNodeData).length > 0 &&
      !baseNodeData.disabled &&
      baseNodeData.baseNodeOption !== 'all'
    ) {
      jsonArray.bn = baseNodeData;
    }

    // Add remote node data
    jsonArray.rn_list = [remoteNodeData];

    // Add operator and email if provided
    if (operator) {
      jsonArray.operator = operator;
    }
    if (email) {
      jsonArray.email = email;
    }

    const optionsParams = API_CONSTANTS.CSV_FILES_FALSE;
    const urlPart = isMultiSector
      ? `${API_CONSTANTS.ENDPOINTS.PREDICT_MULTI_SECTOR}?options=${encodeURIComponent(optionsParams)}`
      : `${API_CONSTANTS.ENDPOINTS.PREDICT_SECTOR}?options=${encodeURIComponent(optionsParams)}`;

    const requestData = {
      url_part: urlPart,
      json_data: jsonArray,
      api_key: keyToUse,
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...(signal && { signal }),
    };

    try {
      const response = (await postRequest({
        config,
      })) as CalculateLostApiResponse;

      if (response.detail) {
        return { error: response.detail };
      }

      return {
        sectorResults: response,
        isMultiSector: !!response.sector_list,
        successful: true,
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true };
      }
      console.error('Error in calculateLost:', error);
      throw error;
    }
  },

  /**
   * Generic method to make SAP requests
   */
  makeSapRequest: async ({
    urlPart,
    jsonData = null,
    apiKey,
    additionalOptions = {},
  }: MakeSapRequestParams): Promise<any> => {
    const keyToUse = apiKey || storedOperatorKey;
    if (!urlPart || !keyToUse) {
      throw new Error(ERROR_MESSAGES.URL_PART_AND_API_KEY_REQUIRED);
    }

    const requestData: any = {
      url_part: urlPart,
      api_key: keyToUse,
      ...(jsonData && { json_data: jsonData }),
    };

    const config: ApiServiceConfig = {
      url: API_CONSTANTS.ENDPOINTS.SAP_REQUEST,
      data: requestData,
      ...additionalOptions,
    };

    try {
      const response = await postRequest({
        config,
      });

      return response;
    } catch (error) {
      console.error('Error in makeSapRequest:', error);
      throw error;
    }
  },

  /**
   * Get SVG data from a URL
   */
  getSVGData: async ({
    url,
    signal,
  }: GetSVGDataParams): Promise<string | { canceled: boolean }> => {
    if (!url) {
      throw new Error(ERROR_MESSAGES.URL_REQUIRED);
    }

    const config: ApiServiceConfig = {
      url: `${API_CONSTANTS.ENDPOINTS.GET_SVG}?url=${encodeURIComponent(url)}`,
      responseType: 'text',
      ...(signal && { signal }),
    };

    try {
      const response = await getRequest({
        config,
      });

      return response;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { canceled: true };
      }
      console.error('Error in getSVGData:', error);
      throw error;
    }
  },
};

export default servicePredictorApiService;
